// frontend/src/pages/employees/PersonView.jsx
import React, { useCallback, useState } from "react";
import BreakdownPanel from "./BreakdownPanel";
import EntriesDrawer from "./EntriesDrawer";
import LeavePanel from "./LeavePanel";
import StatTiles from "./StatTiles";
import TrendStrip from "./TrendStrip";
import { periodLabel } from "./usePeriod";

// The breakdown dimension -> the entries filter it maps to. A breakdown
// row hands back {by, id}; `id` is already the label for a placeholder
// row, which is what the backend reads as "this field is empty".
const FILTER_FOR = { client: "client", time_code: "time_code", task: "task" };

const columns = {
  display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)",
  gap: 24, alignItems: "start",
};

/**
 * One person's page: tiles, breakdown, trend, leave.
 *
 * ONE implementation, two subjects. My Time renders this with the viewer
 * as its subject and Team's drill-in renders it with a manager-selected
 * employee. A second copy for "my own" data would be free to drift from
 * the one a manager sees, and a divergence there is a bug nobody notices
 * until it matters.
 */
export default function PersonView({
  employeeId, name, granularity, anchor, startDate, endDate,
}) {
  const [totals, setTotals] = useState(null);
  const [drawer, setDrawer] = useState(null);

  // Stable, or BreakdownPanel's onTotals effect refires every render.
  const onTotals = useCallback((t) => setTotals(t), []);

  const label = periodLabel(granularity, anchor);

  const openFromRow = useCallback((row) => {
    const params = { start_date: startDate, end_date: endDate };
    if (employeeId) params.employee = employeeId;
    params[FILTER_FOR[row.by]] = row.id;
    setDrawer({
      title: `${row.label} — ${label}`,
      params,
      expectedHours: row.hours,
    });
  }, [employeeId, startDate, endDate, label]);

  const openFromBar = useCallback((bar) => {
    // A bar narrows TIME: its own bounds, and no dimension filter.
    const params = { start_date: bar.startDate, end_date: bar.endDate };
    if (employeeId) params.employee = employeeId;
    setDrawer({ title: bar.label, params, expectedHours: null });
  }, [employeeId]);

  return (
    <div>
      {name && (
        <h3 style={{ margin: "0 0 16px", fontSize: 18 }}>
          {name} — {label}
        </h3>
      )}

      <StatTiles
        hours={totals ? totals.hours : "0.00"}
        billableHours={totals ? totals.billable_hours : "0.00"}
        caption={`in ${label}`}
      />

      <div style={columns}>
        <div>
          <BreakdownPanel
            employeeId={employeeId}
            startDate={startDate}
            endDate={endDate}
            onRowClick={openFromRow}
            onTotals={onTotals}
          />
          <TrendStrip
            granularity={granularity}
            periodLabel={label}
            employeeId={employeeId}
            startDate={startDate}
            endDate={endDate}
            onBarClick={openFromBar}
          />
        </div>

        <LeavePanel
          employeeId={employeeId}
          asOf={endDate}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

      <EntriesDrawer
        open={Boolean(drawer)}
        title={drawer ? drawer.title : ""}
        params={drawer ? drawer.params : {}}
        expectedHours={drawer ? drawer.expectedHours : null}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}
